import type { CSSProperties } from 'react';

import type { Cover } from '../api';

/** Built-in cover gradients (the server stores the preset id: `Domain/Page.cs` PageCovers). */
export const COVER_PRESETS: Record<string, string> = {
  aurora: 'linear-gradient(120deg, #6366f1 0%, #8b5cf6 45%, #ec4899 100%)',
  sunset: 'linear-gradient(120deg, #f97316 0%, #f43f5e 55%, #a855f7 100%)',
  ocean: 'linear-gradient(120deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%)',
  forest: 'linear-gradient(120deg, #10b981 0%, #059669 50%, #0f766e 100%)',
  lavender: 'linear-gradient(120deg, #c4b5fd 0%, #a78bfa 50%, #818cf8 100%)',
  graphite: 'linear-gradient(120deg, #334155 0%, #1e293b 55%, #0f172a 100%)',
  peach: 'linear-gradient(120deg, #fdba74 0%, #fb923c 45%, #f472b6 100%)',
  sky: 'linear-gradient(120deg, #7dd3fc 0%, #38bdf8 50%, #818cf8 100%)',
};

export const COVER_PRESET_IDS = Object.keys(COVER_PRESETS);

/** Background style of a cover (image or gradient); null when the page has none. */
export function coverStyle(cover: Cover | null | undefined): CSSProperties | null {
  if (!cover) return null;
  if (cover.url)
    return {
      backgroundImage: `url("${cover.url.replaceAll('"', '%22')}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  if (cover.preset && COVER_PRESETS[cover.preset]) return { backgroundImage: COVER_PRESETS[cover.preset] };
  return null;
}

/** Emoji offered as page and space icons, by group. */
export const EMOJI_GROUPS: { id: string; emoji: string[] }[] = [
  {
    id: 'docs',
    emoji: [
      '📄',
      '📝',
      '📘',
      '📗',
      '📕',
      '📙',
      '📚',
      '📒',
      '📓',
      '📑',
      '🗂️',
      '📁',
      '📌',
      '📎',
      '🔖',
      '🧾',
      '📋',
      '🗒️',
    ],
  },
  {
    id: 'work',
    emoji: [
      '🚀',
      '🎯',
      '📈',
      '📊',
      '💼',
      '🏢',
      '🧭',
      '🗺️',
      '🛠️',
      '⚙️',
      '🔧',
      '🧪',
      '🔍',
      '💡',
      '✅',
      '☑️',
      '⏱️',
      '📅',
    ],
  },
  {
    id: 'people',
    emoji: [
      '👋',
      '🤝',
      '👥',
      '🧑‍💻',
      '👩‍💼',
      '🧑‍🏫',
      '🎓',
      '🏆',
      '🎉',
      '❤️',
      '🙌',
      '💬',
      '📣',
      '📢',
      '🎁',
      '☕',
      '🍀',
      '🌟',
    ],
  },
  {
    id: 'tech',
    emoji: [
      '💻',
      '🖥️',
      '📱',
      '⌨️',
      '🖨️',
      '📶',
      '🔐',
      '🔑',
      '🛡️',
      '☁️',
      '🧩',
      '🐞',
      '🤖',
      '🔌',
      '💾',
      '🧰',
      '📡',
      '🔒',
    ],
  },
  {
    id: 'life',
    emoji: [
      '🏖️',
      '✈️',
      '🏠',
      '🚲',
      '🚗',
      '🍽️',
      '🏃',
      '🧘',
      '🤒',
      '💊',
      '🌱',
      '🌍',
      '🎨',
      '🎵',
      '📷',
      '⚽',
      '🎄',
      '💰',
    ],
  },
];

export const DEFAULT_PAGE_ICON = '📄';
